// src/utils/attribution.js
//
// Remembers how a visitor arrived, so "where do my visitors come from?" has an
// answer.
//
// Two signals, both of which decay the moment the app starts routing:
//
//   * the UTM tags, which only exist on the URL the visitor actually landed on
//   * document.referrer, which reports OUR own origin once React Router has
//     navigated even once
//
// So both are read as early as possible and pinned in sessionStorage for the
// rest of the visit. First touch wins: if someone arrives from Instagram and
// then clicks around, every event in that session is still credited to
// Instagram rather than to the last page they happened to be on.
//
// sessionStorage (not localStorage) on purpose — a new visit tomorrow from a
// different place should be attributed to that place, not to this one.
import { safeSessionStorage } from './safeStorage';

const KEY = 'mintlink_attribution';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

let cached = null;

function readStored() {
  try {
    const raw = safeSessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Capture attribution for this session. Safe to call on every page — it only
 * writes the first time, which is what makes it first-touch.
 */
export function captureAttribution() {
  if (cached) return cached;

  const stored = readStored();
  if (stored) {
    cached = stored;
    return cached;
  }

  const data = {};
  try {
    const params = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) data[k] = v.slice(0, 120);
    });

    // Only an external referrer tells us anything. Our own origin means the
    // visitor was already here, and recording that would drown the real
    // sources in self-referrals.
    const ref = document.referrer || '';
    if (ref && !ref.includes(window.location.host)) {
      data.referrer = ref.slice(0, 300);
    }
  } catch {
    // Storage or URL parsing blocked — attribution is optional, never fatal.
  }

  cached = data;
  try {
    safeSessionStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* private mode / quota — keep the in-memory copy for this page at least */
  }
  return cached;
}

/**
 * What we know about where this visitor came from. `{}` when there's nothing —
 * the backend reads that as "direct", which is the correct answer.
 */
export function getAttribution() {
  return cached || readStored() || captureAttribution();
}
